const DESKTOP_QUERY = '(min-width: 750px)';
const SELECT_EVENT = 'slideshow:select';
const VISIBLE_RATIO = 0.25;

/**
 * Background media of an MX slide. Media are kept in <template> elements and inserted
 * only when the slide is needed and only for the current breakpoint. Inserted media stay
 * in the DOM, so looping back to a slide never downloads them again.
 */
class MxSlideMedia extends HTMLElement {
  #desktopQuery = window.matchMedia(DESKTOP_QUERY);
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #loaded = false;
  #active = false;
  #inView = true;
  #videos = new Map();

  connectedCallback() {
    this.#desktopQuery.addEventListener('change', this.#handleBreakpointChange);

    // The slideshow moves slides in the DOM when it loops, which pauses their videos.
    this.#syncPlayback();
  }

  disconnectedCallback() {
    this.#desktopQuery.removeEventListener('change', this.#handleBreakpointChange);
  }

  get active() {
    return this.#active;
  }

  set active(value) {
    this.#active = value;
    if (value) this.load();
    this.#syncPlayback();
  }

  set inView(value) {
    this.#inView = value;
    this.#syncPlayback();
  }

  load() {
    if (!this.#loaded) {
      this.#loaded = true;
      const still = this.querySelector(':scope > template[data-still]');
      if (still) this.prepend(still.content.cloneNode(true));
    }

    this.#showVideo();
  }

  get #breakpoint() {
    return this.#desktopQuery.matches ? 'desktop' : 'mobile';
  }

  #showVideo() {
    const key = this.querySelector(`:scope > template[data-video="${this.#breakpoint}"]`) ? this.#breakpoint : 'all';

    if (!this.#videos.has(key)) {
      const template = this.querySelector(`:scope > template[data-video="${key}"]`);
      const video = template?.content.querySelector('video');
      if (video) {
        const clone = /** @type {HTMLVideoElement} */ (document.importNode(video, true));
        clone.muted = true;
        clone.playsInline = true;
        this.append(clone);
        this.#videos.set(key, clone);
      }
    }

    for (const [videoKey, video] of this.#videos) {
      video.hidden = videoKey !== key;
    }

    this.#syncPlayback();
  }

  #syncPlayback() {
    for (const video of this.#videos.values()) {
      if (this.#active && this.#inView && !video.hidden && !this.#motionQuery.matches) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  }

  #handleBreakpointChange = () => {
    if (this.#loaded) this.#showVideo();
  };
}

/**
 * Wraps a slideshow-component and loads slide media on demand: the current slide and the
 * one after it. Autoplay and the first video start after the page loads (or on the first
 * interaction with the slideshow). Suspends autoplay and videos while the slideshow is
 * scrolled out of view and stops autoplay on the last slide when looping is turned off.
 */
class MxSlideshow extends HTMLElement {
  /** @type {MxSlideMedia[]} */
  #items = [];
  #inView = true;
  #observer = new MutationObserver(() => this.#handlePlay());
  #viewObserver = new IntersectionObserver(([entry]) => this.#handleView(entry), {
    threshold: [0, VISIBLE_RATIO],
  });

  connectedCallback() {
    this.addEventListener(SELECT_EVENT, this.#handleSelect);
    this.addEventListener('mouseleave', this.#keepSuspended);
    document.addEventListener('visibilitychange', this.#keepSuspended);
    this.#viewObserver.observe(this);
    this.#items = [...this.querySelectorAll('mx-slide-media')];

    const { slideshow } = this;
    if (slideshow) this.#observer.observe(slideshow, { attributes: true, attributeFilter: ['paused'] });

    if (document.readyState === 'complete') {
      // Inserted after load (theme editor): let the child elements upgrade first.
      queueMicrotask(this.#handleLoad);
    } else {
      window.addEventListener('load', this.#handleLoad, { once: true });
      // Autoplay must be on before the play button is used, otherwise it plays with no interval.
      this.addEventListener('pointerdown', this.#startAutoplay, { capture: true });
      this.addEventListener('focusin', this.#startAutoplay, { capture: true });
    }
  }

  disconnectedCallback() {
    this.removeEventListener(SELECT_EVENT, this.#handleSelect);
    this.removeEventListener('mouseleave', this.#keepSuspended);
    document.removeEventListener('visibilitychange', this.#keepSuspended);
    window.removeEventListener('load', this.#handleLoad);
    this.#removeStartListeners();
    this.#viewObserver.disconnect();
    this.#observer.disconnect();
  }

  /** @returns {(HTMLElement & { pause(): void, suspend(): void, resume(): void, select(index: number): void, infinite: boolean, autoplay: boolean, paused: boolean, atEnd: boolean }) | null} */
  get slideshow() {
    return this.querySelector('slideshow-component');
  }

  #handleLoad = () => {
    let index = this.#items.findIndex((item) => item.active);
    if (index === -1) {
      index = 0;
      this.#activate(this.#items[0]);
    }
    this.#preloadNext(index);
    this.#startAutoplay();
  };

  #startAutoplay = () => {
    this.#removeStartListeners();
    const { slideshow } = this;
    const speed = this.dataset.autoplay;
    if (!slideshow || !speed || slideshow.hasAttribute('autoplay')) return;

    slideshow.setAttribute('autoplay', speed);
    // Not upgraded yet: the slideshow starts autoplay itself once it connects.
    if (this.#inView) slideshow.resume?.();
  };

  #removeStartListeners() {
    this.removeEventListener('pointerdown', this.#startAutoplay, { capture: true });
    this.removeEventListener('focusin', this.#startAutoplay, { capture: true });
  }

  /** @param {MxSlideMedia | undefined} current */
  #activate(current) {
    for (const item of this.#items) item.active = item === current;
  }

  /** @param {number} index */
  #preloadNext(index) {
    const items = this.#items;
    const next = index + 1 < items.length ? items[index + 1] : this.slideshow?.infinite ? items[0] : null;
    next?.load();
  }

  /** @param {Event} event */
  #handleSelect = (event) => {
    const { detail } = /** @type {CustomEvent} */ (event);
    const current = detail?.slide?.querySelector('mx-slide-media');
    if (!current) return;

    this.#activate(current);
    this.#preloadNext(this.#indexOf(detail.slide));

    const { slideshow } = this;
    if (slideshow && !slideshow.infinite && slideshow.autoplay && slideshow.atEnd) slideshow.pause();
  };

  #handlePlay() {
    const { slideshow } = this;
    if (!slideshow || slideshow.infinite || !slideshow.autoplay || slideshow.paused) return;

    if (slideshow.atEnd) slideshow.select(0);
  }

  /** @param {IntersectionObserverEntry} entry */
  #handleView(entry) {
    const inView = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;
    if (inView === this.#inView) return;
    this.#inView = inView;

    for (const item of this.#items) item.inView = inView;

    const { slideshow } = this;
    if (!slideshow?.autoplay) return;
    if (inView) {
      slideshow.resume();
    } else {
      slideshow.suspend();
    }
  }

  // The slideshow resumes autoplay on mouseleave and when the tab becomes visible again,
  // even when it is out of view. Runs after its own handlers.
  #keepSuspended = () => {
    requestAnimationFrame(() => {
      if (!this.#inView) this.slideshow?.suspend();
    });
  };

  /** @param {Element} slide */
  #indexOf(slide) {
    return this.#items.findIndex((item) => slide.contains(item));
  }
}

if (!customElements.get('mx-slide-media')) customElements.define('mx-slide-media', MxSlideMedia);
if (!customElements.get('mx-slideshow')) customElements.define('mx-slideshow', MxSlideshow);
